import { BKJButtonIconTextInput, Flex } from '@bookjane2/bookjane-design-library';
import { FetchAgencyContextType } from 'modals/ShiftModal/components/AgencyGridSelect/AgencyGridSelect.types';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { QuerySwitch } from 'components/QuerySwitch';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { RequestTeamMemberFilterBar } from 'modals/ShiftModal/src/views/SendToAgenciesView/RequestTeamMemberView/RequestTeamMemberFilterBar';
import { PriceFilter } from 'modals/ShiftModal/src/views/SendToAgenciesView/RequestTeamMemberView/RequestTeamMemberView.styled';
import { useSendToAgenciesFormContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.form.context';
import { useSendToAgenciesPropsContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SendToAgencies.props.context';
import {
  FetchCombinedCommunitiesByOrderIdDataContext,
  useFetchCombinedCommunitiesByOrderIdDataContext,
} from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/contexts/FetchCombinedCommunitiesByOrderId.data.context';
import { useSelectAgenciesViewPropsContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/contexts/SelectAgenciesView.props.context';
import { useTranslation } from 'hooks/useTranslation';
import {
  AgencySelectListWrapper,
  AvailableCountText,
  EstimatedTotalPayoutFooterText,
  HeaderWrapper,
  MainTitleContainer,
  Title,
  NextButton,
} from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/SendToSelectedAgenciesView.styled';
import { UseFormBehaviorsEventType } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { Fragment, useCallback, useMemo } from 'react';
import { AgencyGridSelect } from 'modals/ShiftModal/components/AgencyGridSelect';
import { useFetchCombinedRatesDataContext } from 'modals/ShiftModal/src/views/SendToAgenciesView/SelectAgenciesView/contexts/FetchCombinedRates.data.context';
import { FormatCurrency } from 'components/FormatCurrency';

export const SelectAgenciesViewComponent = () => {
  const { t } = useTranslation();
  const { goToNextView } = useSendToAgenciesPropsContext();
  const { values: formValues, onChange: _onChangeForm } = useSendToAgenciesFormContext();
  const { isOpen, handleFilterClick, fetchAgenciesNoAgenciesErrorMessage } =
    useSelectAgenciesViewPropsContext();

  const { data: combinedRate } = useFetchCombinedRatesDataContext();

  const {
    onChange: onChangeSearchFilter,
    values: fetchAgenciesFilterValues,
    data,
  } = useFetchCombinedCommunitiesByOrderIdDataContext();

  const { data: shiftModalData } = useShiftModalDataContext();
  const order = shiftModalData?.order;
  const positionId = order?.position.displayId;
  const marketplacePriceRange = order?.position.marketplacePriceRange;

  const totalAvailableCount = useMemo(() => {
    const marketplaceCount = data?.fetchMarketplaceCommunities?.totalCount || 0;
    const agencyCount = data?.fetchAgencyCommunities?.totalCount || 0;
    return marketplaceCount + agencyCount;
  }, [data]);

  const onChangeForm = useCallback(
    (e: UseFormBehaviorsEventType) => {
      const { name, value } = e.target;
      const fieldName = name as 'selectedAgencies' | 'selectedMarketplaceCommunities';
      const currentSelected = formValues[fieldName];
      const agencyId = value as number;

      if (currentSelected.includes(agencyId)) {
        _onChangeForm({
          target: {
            name: fieldName,
            value: currentSelected.filter((id) => id !== agencyId),
          },
        });
      } else {
        _onChangeForm({
          target: {
            name: fieldName,
            value: [...currentSelected, agencyId],
          },
        });
      }
    },
    [_onChangeForm, formValues],
  );

  const selectedCombined = useMemo(
    () => [...formValues.selectedAgencies, ...formValues.selectedMarketplaceCommunities],
    [formValues.selectedAgencies, formValues.selectedMarketplaceCommunities],
  );

  const AgencyGridSelectComponent = useCallback(
    () => (
      <AgencyGridSelect
        name="selectedAgencies"
        selected={selectedCombined}
        type="Multiple"
        errorMessage={fetchAgenciesNoAgenciesErrorMessage}
        onChangeForm={onChangeForm}
        positionId={positionId}
        fetchAgenciesContext={
          FetchCombinedCommunitiesByOrderIdDataContext as unknown as FetchAgencyContextType
        }
      />
    ),
    [fetchAgenciesNoAgenciesErrorMessage, selectedCombined, onChangeForm, positionId],
  );

  return (
    <Fragment>
      <HeaderWrapper>
        <MainTitleContainer justifyContent="space-between" alignItems="baseline">
          <Title>{t('shift_modal:CHOOSE_AGENCIES')}</Title>
          {totalAvailableCount > 0 && (
            <AvailableCountText>{`${totalAvailableCount} ${t(
              'shift_modal:AGENCIES_AVAILABLE',
            )}`}</AvailableCountText>
          )}
        </MainTitleContainer>
        <Flex flexDirection="column">
          <BKJButtonIconTextInput
            placeholder={t('shift_modal:SEARCH_AGENCY')}
            buttonLabel={t('shift_modal:FILTERS')}
            name="byName"
            iconName="Search"
            buttonIcon="Filters"
            value={fetchAgenciesFilterValues.byName}
            onChange={onChangeSearchFilter}
            onButtonClick={handleFilterClick}
            autoComplete="off"
            label=""
            required={false}
            error={''}
            disabled={false}
          />
          <RequestTeamMemberFilterBar isOpen={isOpen}>
            {marketplacePriceRange?.min && marketplacePriceRange?.max && (
              <PriceFilter
                priceRange={[marketplacePriceRange.min, marketplacePriceRange.max]}
                name="byRateLessThanOrEqualTo"
                value={[fetchAgenciesFilterValues.byRateLessThanOrEqualTo]}
                onChange={onChangeSearchFilter}
              />
            )}
          </RequestTeamMemberFilterBar>
        </Flex>
      </HeaderWrapper>

      <AgencySelectListWrapper>
        <QuerySwitch
          context={FetchCombinedCommunitiesByOrderIdDataContext}
          component={AgencyGridSelectComponent}
        />
      </AgencySelectListWrapper>

      <BKJSideModalFooter justifyContent="space-between">
        <EstimatedTotalPayoutFooterText disabled={selectedCombined.length === 0}>
          Est. {t('common:TOTAL_PAY')}:
          {!!combinedRate?.cost ? (
            combinedRate.cost.min === combinedRate.cost.max ? (
              <FormatCurrency amount={`${combinedRate.cost.min}`} />
            ) : (
              <Fragment>
                <FormatCurrency amount={`${combinedRate.cost.min}`} /> -
                <FormatCurrency amount={`${combinedRate.cost.max}`} />
              </Fragment>
            )
          ) : (
            <FormatCurrency amount="0.0" />
          )}
        </EstimatedTotalPayoutFooterText>
        <NextButton
          width="180px"
          variant="PurpleSolid"
          onClick={goToNextView}
          disabled={selectedCombined.length === 0}
        >
          {t('shift_modal:NEXT')}
        </NextButton>
      </BKJSideModalFooter>
    </Fragment>
  );
};
